import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { permDe } from "./utils/permissoes";
import { AppProvider, useApp } from "./context/AppContext";
import { Toaster } from "./components/ui/toaster";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import PainelAssistencial from "./pages/PainelAssistencial";
import Residentes from "./pages/Residentes";
import ResidenteDetalhe from "./pages/ResidenteDetalhe";
import Prontuario from "./pages/Prontuario";
import FornecimentoFamiliar from "./pages/FornecimentoFamiliar";
import Consumo from "./pages/Consumo";
import Estoque from "./pages/Estoque";
import Produtos from "./pages/Produtos";
import Faturas from "./pages/Faturas";
import Fornecedores from "./pages/Fornecedores";
import ContasPagar from "./pages/ContasPagar";
import RelatoriosFinanceiros from "./pages/RelatoriosFinanceiros";
import Inventario from "./pages/Inventario";
import Mensagens from "./pages/Mensagens";
import FamiliaPortal from "./pages/FamiliaPortal";
import Notificacoes from "./pages/Notificacoes";
import PainelEnvios from "./pages/PainelEnvios";import Usuarios from "./pages/Usuarios";
import Profissionais from "./pages/Profissionais";
import PainelTV from "./pages/PainelTV";
import Configuracoes from "./pages/Configuracoes";
import Integracoes from "./pages/Integracoes";
import ServicosExtras from "./pages/ServicosExtras";
import MeuPerfil from "./pages/MeuPerfil";
import PainelAnalitico from "./pages/PainelAnalitico";
import PoliticaPrivacidade from "./pages/PoliticaPrivacidade";

const MOD_ROUTE = { dashboard: "/dashboard", analitico: "/analitico", painel: "/painel", residentes: "/residentes", fornecimento: "/fornecimento", consumo: "/consumo", estoque: "/estoque", produtos: "/produtos", financeiro: "/faturas", inventario: "/inventario", mensagens: "/mensagens", profissionais: "/profissionais", notificacoes: "/notificacoes", usuarios: "/usuarios", configuracoes: "/configuracoes", integracoes: "/integracoes" };
const MOD_ORDER = ["painel", "residentes", "fornecimento", "consumo", "produtos", "estoque", "dashboard", "analitico", "financeiro", "inventario", "mensagens", "profissionais", "notificacoes", "usuarios", "configuracoes", "integracoes"];

const landingFor = (auth) => {
  if (!auth) return "/login";
  if (auth.perfil === "familia") return "/familia";
  if (auth.perfil === "administrador") return "/dashboard";
  const perms = auth.permissoes;
  if (perms && Object.keys(perms).length > 0) {
    for (const m of MOD_ORDER) if (perms[m]?.ver) return MOD_ROUTE[m];
    return "/meu-perfil";
  }
  return "/painel";
};

const Protected = ({ children }) => {
  const { auth } = useApp();
  if (!auth) return <Navigate to="/login" replace />;
  if (auth.perfil === "familia") return <Navigate to="/familia" replace />;
  return children;
};

// Guard ciente de permissões: se o usuário tem permissões personalizadas, o acesso
// segue permissoes[modulo].ver; caso contrário usa o padrão por papel (roles/financeiro).
const Perm = ({ modulo, roles, financeiro, children }) => {
  const { auth } = useApp();
  if (!auth) return <Navigate to="/login" replace />;
  if (auth.perfil === "familia") return <Navigate to="/familia" replace />;
  if (auth.perfil === "administrador") return children;
  const perms = auth.permissoes;
  const custom = perms && Object.keys(perms).length > 0;
  if (custom) {
    if (modulo === "tv" && !perms.tv) return children; // sem chave "tv" mantém o acesso antigo
    return modulo && permDe(perms, modulo)?.ver ? children : <Navigate to={landingFor(auth)} replace />;
  }
  if (financeiro) return auth.acessoFinanceiro ? children : <Navigate to={landingFor(auth)} replace />;
  if (roles && !roles.includes(auth.perfil)) return <Navigate to={landingFor(auth)} replace />;
  return children;
};

const HomeRedirect = () => {
  const { auth } = useApp();
  return <Navigate to={landingFor(auth)} replace />;
};

const FamiliaOnly = ({ children }) => {
  const { auth } = useApp();
  if (!auth) return <Navigate to="/login" replace />;
  if (auth.perfil !== "familia") return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
      <Route path="/familia" element={<FamiliaOnly><FamiliaPortal /></FamiliaOnly>} />
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/dashboard" element={<Perm modulo="dashboard" roles={["administrador"]}><Dashboard /></Perm>} />
        <Route path="/analitico" element={<Perm modulo="analitico" roles={["administrador"]}><PainelAnalitico /></Perm>} />
        <Route path="/painel" element={<Perm modulo="painel"><PainelAssistencial /></Perm>} />
        <Route path="/residentes" element={<Perm modulo="residentes"><Residentes /></Perm>} />
        <Route path="/residentes/:id" element={<Perm modulo="residentes"><ResidenteDetalhe /></Perm>} />
        <Route path="/residentes/:id/prontuario" element={<Perm modulo="prontuario"><Prontuario /></Perm>} />
        <Route path="/fornecimento" element={<Perm modulo="fornecimento"><FornecimentoFamiliar /></Perm>} />
        <Route path="/consumo" element={<Perm modulo="consumo"><Consumo /></Perm>} />
        <Route path="/estoque" element={<Perm modulo="estoque" roles={["administrador"]}><Estoque /></Perm>} />
        <Route path="/produtos" element={<Perm modulo="produtos" roles={["administrador", "enfermeiro"]}><Produtos /></Perm>} />
        <Route path="/faturas" element={<Perm modulo="faturas" financeiro><Faturas /></Perm>} />
        <Route path="/financeiro/fornecedores" element={<Perm modulo="fornecedores" financeiro><Fornecedores /></Perm>} />
        <Route path="/financeiro/contas-pagar" element={<Perm modulo="contas_pagar" financeiro><ContasPagar /></Perm>} />
        <Route path="/financeiro/servicos-extras" element={<Perm modulo="servicos_extras" financeiro><ServicosExtras /></Perm>} />
        <Route path="/financeiro/relatorios" element={<Perm modulo="dre" financeiro><RelatoriosFinanceiros /></Perm>} />
        <Route path="/meu-perfil" element={<MeuPerfil />} />
        <Route path="/inventario" element={<Perm modulo="inventario" roles={["administrador"]}><Inventario /></Perm>} />
        <Route path="/inventario/:id" element={<Perm modulo="inventario" roles={["administrador"]}><Inventario /></Perm>} />
        <Route path="/mensagens" element={<Perm modulo="mensagens" roles={["administrador", "enfermeiro"]}><Mensagens /></Perm>} />
        <Route path="/notificacoes" element={<Perm modulo="notificacoes" roles={["administrador"]}><Notificacoes /></Perm>} />
        <Route path="/painel-envios" element={<Perm modulo="envios" roles={["administrador"]}><PainelEnvios /></Perm>} />
        <Route path="/usuarios" element={<Perm modulo="usuarios" roles={["administrador"]}><Usuarios /></Perm>} />
        <Route path="/configuracoes" element={<Perm modulo="configuracoes" roles={["administrador"]}><Configuracoes /></Perm>} />
        <Route path="/integracoes" element={<Perm modulo="integracoes" roles={["administrador"]}><Integracoes /></Perm>} />
        <Route path="/profissionais" element={<Navigate to="/usuarios?aba=profissionais" replace />} />
      </Route>
      <Route path="/painel-tv" element={<Perm modulo="tv"><PainelTV /></Perm>} />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

// Silencia o aviso inofensivo do dev-server que bloqueia cliques no preview.
if (typeof window !== "undefined") {
  const RO_MSG = "ResizeObserver loop";
  window.addEventListener("error", (e) => {
    if (e?.message && e.message.includes(RO_MSG)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster />
        </BrowserRouter>
      </AppProvider>
    </div>
  );
}

export default App;
